import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Button,
  Stack,
  Divider,
  Chip,
  Avatar,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Badge,
  Alert,
  CircularProgress,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Forum';
import DealIcon from '@mui/icons-material/Handshake';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import HistoryIcon from '@mui/icons-material/History';
import SendIcon from '@mui/icons-material/Send';
import InfoIcon from '@mui/icons-material/Info';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecureIcon from '@mui/icons-material/Security';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { useShop } from '../../context/ShopContext';
import { supabase } from '../../services/supabaseClient';
import { NotificationService } from '../../services/errorService';

const DealRoom: React.FC = () => {
  const { activeShop } = useShop();
  const [message, setMessage] = useState('');
  const [deals, setDeals] = useState<any[]>([]);
  const [selectedDeal, setSelectedDeal] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDeals = async () => {
      if (!activeShop) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('b2b_negotiations')
          .select('*')
          .eq('shop_id', activeShop.id)
          .order('updated_at', { ascending: false });

        if (error) throw error;
        setDeals(data || []);
        if (data && data.length > 0) {
          setSelectedDeal(data[0]);
        }
      } catch (error) {
        console.error('Error fetching deals:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDeals();
  }, [activeShop]);

  const handleSendMessage = async () => {
    if (!message.trim() || !selectedDeal) return;
    
    try {
      const updatedMessages = [...(selectedDeal.messages || []), {
        role: 'seller',
        text: message,
        time: new Date().toISOString()
      }];

      const { error } = await supabase
        .from('b2b_negotiations')
        .update({ 
          messages: updatedMessages,
          updated_at: new Date().toISOString(),
          last_message: message // We might need to add this column or just use the array
        })
        .eq('id', selectedDeal.id);

      if (error) throw error;
      
      setSelectedDeal({ ...selectedDeal, messages: updatedMessages });
      setMessage('');
      NotificationService.success('Success', 'Message sent securely.');
    } catch (error) {
      NotificationService.error('Error', 'Failed to send message.');
    }
  };

  const mockDeals = [
    {
      id: 'DEAL-001',
      buyer: 'Grand Nairobi Hotel',
      product: 'Organic Tomatoes',
      quantity: '500 kg',
      offeredPrice: 'KSh 85 / kg',
      status: 'negotiating',
      lastMessage: 'Can you deliver by Friday morning?',
      time: '2 mins ago',
    },
    {
      id: 'DEAL-002',
      buyer: 'Fresh Markets Ltd',
      product: 'Hybrid Maize Seeds',
      quantity: '2,000 units',
      offeredPrice: 'KSh 1,200 / unit',
      status: 'pending_approval',
      lastMessage: 'Price accepted. Awaiting contract.',
      time: '1 hour ago',
    },
  ];

  if (!activeShop) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', py: 10 }}>
        <DealIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" gutterBottom>Select a Shop to Enter the Deal Room</Typography>
        <Typography variant="body1" color="text.secondary">
          The Deal Room is where large-scale B2B negotiations happen.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            B2B Deal Room <DealIcon color="primary" />
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Secure wholesale negotiations for <strong>{activeShop.name}</strong>
          </Typography>
        </Box>
        <Chip label="B2B Verified" color="success" sx={{ fontWeight: 'bold' }} />
      </Stack>

      <Grid container spacing={3}>
        {/* Active Negotiations List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ borderRadius: '16px', height: '70vh', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" fontWeight="bold">Active Deals</Typography>
            </Box>
            <List sx={{ flexGrow: 1, overflow: 'auto' }}>
              {deals.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No active B2B deals.</Typography>
                </Box>
              ) : deals.map((deal) => (
                <ListItemButton 
                  key={deal.id} 
                  divider
                  onClick={() => setSelectedDeal(deal)}
                  sx={{ 
                    '&:hover': { bgcolor: 'action.hover' },
                    bgcolor: selectedDeal?.id === deal.id ? 'primary.50' : 'inherit'
                  }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography fontWeight="bold">Buyer #{deal.buyer_id.slice(0, 5)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(deal.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="body2" color="text.primary" noWrap>
                          {deal.quantity} {deal.unit} @ KSh {deal.offered_price}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip 
                            label={deal.status.replace('_', ' ').toUpperCase()} 
                            size="small" 
                            color={deal.status === 'negotiating' ? 'primary' : 'warning'} 
                            sx={{ height: 20, fontSize: '10px' }} 
                          />
                        </Box>
                      </Box>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Negotiation Workspace */}
        <Grid item xs={12} md={8}>
          {selectedDeal ? (
            <Paper sx={{ borderRadius: '16px', height: '70vh', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50' }}>
              {/* Deal Header */}
              <Box sx={{ p: 2, bgcolor: 'white', borderBottom: 1, borderColor: 'divider', borderRadius: '16px 16px 0 0' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main' }}>B</Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">Buyer #{selectedDeal.buyer_id.slice(0, 5)}</Typography>
                      <Typography variant="caption" color="text.secondary">Secure B2B Negotiation</Typography>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button variant="outlined" color="error" size="small">Reject</Button>
                    <Button variant="contained" color="success" size="small" startIcon={<DealIcon />}>Finalize</Button>
                  </Stack>
                </Stack>
              </Box>

              {/* Negotiation History / Chat */}
              <Box sx={{ flexGrow: 1, p: 3, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
                  <strong>Security Alert:</strong> Never share phone numbers or personal emails. Direct payment outside Farmers HUB voids your insurance and buyer protection.
                </Alert>
                
                {selectedDeal.messages && selectedDeal.messages.map((msg: any, idx: number) => (
                  <Box 
                    key={idx} 
                    sx={{ 
                      alignSelf: msg.role === 'seller' ? 'flex-end' : 'flex-start',
                      maxWidth: '70%',
                      bgcolor: msg.role === 'seller' ? 'primary.main' : 'white',
                      color: msg.role === 'seller' ? 'white' : 'text.primary',
                      p: 2,
                      borderRadius: msg.role === 'seller' ? '12px 12px 0 12px' : '12px 12px 12px 0',
                      boxShadow: 1
                    }}
                  >
                    <Typography variant="body2">{msg.text}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.5, textAlign: 'right' }}>
                      {new Date(msg.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Negotiation Controls */}
              <Box sx={{ p: 2, bgcolor: 'white', borderTop: 1, borderColor: 'divider', borderRadius: '0 0 16px 16px' }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Paper variant="outlined" sx={{ px: 2, py: 1, bgcolor: 'primary.50', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" fontWeight="bold">Deal Value:</Typography>
                      <Typography variant="body2" color="primary.dark" fontWeight="bold">KSh {selectedDeal.deal_value?.toLocaleString()}</Typography>
                    </Paper>
                    <Paper variant="outlined" sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" fontWeight="bold">Platform Fee (5%):</Typography>
                      <Typography variant="body2" color="warning.dark" fontWeight="bold">KSh {selectedDeal.platform_fee_estimated?.toLocaleString()}</Typography>
                    </Paper>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Type your message or counter-offer..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && handleSendMessage()}
                    />
                    <IconButton 
                      color="primary" 
                      onClick={handleSendMessage}
                      disabled={!message.trim()}
                      sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}
                    >
                      <SendIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          ) : (
            <Paper sx={{ borderRadius: '16px', height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50' }}>
              <Box sx={{ textAlign: 'center' }}>
                <ChatIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">Select a deal to view the negotiation history</Typography>
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Deal Room Insights */}
      <Box sx={{ mt: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: '16px', display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ bgcolor: 'warning.light', p: 2, borderRadius: '50%' }}>
                <TrendingUpIcon sx={{ color: 'warning.dark' }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">Wholesale Demand</Typography>
                <Typography variant="body2" color="text.secondary">Demand for "Tomatoes" from hotels is up 25% this week. Keep your prices competitive.</Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: '16px', display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ bgcolor: 'info.light', p: 2, borderRadius: '50%' }}>
                <MoneyIcon sx={{ color: 'info.dark' }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">Projected Deal Value</Typography>
                <Typography variant="body2" color="text.secondary">You have KSh 425,000 in potential revenue currently under negotiation.</Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default DealRoom;
